import React, { useEffect, useState } from 'react';
// import './Backoffice.css'; // Removed to use App.css

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

function resolveEventDomain() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_EVENT_DOMAIN || '';
  }
  return hostname.replace(/^www\./, '');
}
const EVENT_DOMAIN = resolveEventDomain();

// All hooks are called at the top level, not conditionally.
function Backoffice({ eventConfig }) {
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('backofficeToken') || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [filter, setFilter] = useState('all');

  // State for Edit/Create modal
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', phone: '', attendance: 'both', guests: 1, children: 0, food: '', message: ''
  });

  const foodOptions = eventConfig?.foodOptions || [];

  const attendanceMap = Object.fromEntries(
    (eventConfig?.attendanceOptions || []).map(o => [o.value, o.label])
  );

  const getRowStyle = (attendance) => {
    switch (attendance) {
      case 'both':
        return { backgroundColor: '#cfe2ff' }; // Light blue
      case 'restaurant':
      case 'synagogue':
        return { backgroundColor: '#e7f1ff' }; // Lighter blue
      case 'no':
        return { backgroundColor: '#eee' }; // White
      case 'maybe':
        return { backgroundColor: '#e2e3e5' }; // Gray
      default:
        return {};
    }
  };  

  // Calculate summary
  const summary = rsvps.reduce((acc, r) => {
    const guests = (r.Guests || 0);
    const children = (r.Children || 0);
    
    const isAttending = ['both', 'restaurant', 'synagogue'].includes(r.Attendance);
    if (isAttending) {
      acc.attending++;
      acc.guests += guests;
      acc.children += children;
    }

    if (r.Attendance === 'both' || r.Attendance === 'synagogue') {
      acc.synagogue.guests += guests;
      acc.synagogue.children += children;
    }

    if (r.Attendance === 'both' || r.Attendance === 'restaurant') {
      acc.restaurant.guests += guests;
      acc.restaurant.children += children;
    }

    if (r.Attendance === 'no') acc.notComing++;
    if (r.Attendance === 'maybe') acc.maybe++;

    return acc;
  }, { 
    attending: 0, 
    guests: 0, 
    children: 0, 
    synagogue: { guests: 0, children: 0 }, 
    restaurant: { guests: 0, children: 0 },
    notComing: 0,
    maybe: 0
  });

  // Filter RSVPs
  const filteredRsvps = rsvps.filter(r => {
    if (filter === 'all') return true;
    return r.Attendance === filter;
  });

  useEffect(() => {
    if (token) fetchRsvps();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${SERVER_URL}/api/backoffice/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, eventDomain: EVENT_DOMAIN })
      });
      const data = await res.json();
      if (data.token) {
        setToken(data.token);
        localStorage.setItem('backofficeToken', data.token);
        setError('');
      } else {
        setError('סיסמה שגויה');
      }
    } catch (err) {
      setError('שגיאת תקשורת');
    }
  };

  const fetchRsvps = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rsvps`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401) {
        logout();
        return;
      }
      const data = await res.json();
      setRsvps(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const logout = () => {
    setToken('');
    localStorage.removeItem('backofficeToken');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם למחוק רשומה זו?')) return;
    try {
      await fetch(`${SERVER_URL}/api/rsvp/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchRsvps();
    } catch (err) {
      alert('שגיאה במחיקה');
    }
  };

  const handleEdit = (rsvp) => {
    setIsEditing(true);
    setCurrentId(rsvp.ID);
    setFormData({
      name: rsvp.Name,
      phone: rsvp.Phone,
      attendance: rsvp.Attendance,
      guests: rsvp.Guests,
      children: rsvp.Children,
      food: rsvp.Food,
      message: rsvp.Message
    });
  };

  const handleCreate = () => {
    setIsEditing(true);
    setCurrentId(null);
    setFormData({
      name: '', phone: '', attendance: 'both', guests: 1, children: 0, food: '', message: ''
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const url = currentId 
      ? `${SERVER_URL}/api/rsvp/${currentId}`
      : `${SERVER_URL}/api/rsvp/admin`;
    
    const method = currentId ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setIsEditing(false);
        fetchRsvps();
      } else {
        alert('שגיאה בשמירה');
      }
    } catch (err) {
      alert('שגיאה בשמירה');
    }
  };

  if (!token) {
    return (
      <div className="login-container">
        <form onSubmit={login} className="login-form">
          <h2>כניסה למערכת ניהול</h2>
          <input 
            type="password" 
            value={password} 
            onChange={e => setPassword(e.target.value)} 
            placeholder="סיסמה"
            className="rsvp-input"
          />
          <button type="submit" className="rsvp-btn">כניסה</button>
          {loginError && <div className="error">{loginError}</div>}
        </form>
      </div>
    );
  }

  return (
    <div className="backoffice-wrapper">
      <div className="backoffice-header">
        <h2>📋 ניהול אישורי הגעה ({rsvps.length})</h2>
        <div>
          <button onClick={handleCreate} className="action-btn create-btn">➕ הוסף חדש</button>
          <button onClick={logout} className="action-btn logout-btn">🚪 יציאה</button>
        </div>
      </div>

      <div className="summary-container">
        <div className="summary-card">
          <div className="summary-value">{rsvps.length}</div>
          <div className="summary-label">📝 סה"כ תגובות</div>
        </div>
        
        <div className="summary-card">
          <div className="summary-label" style={{marginBottom: '10px', fontWeight: 'bold'}}>🕍 לבית הכנסת ({summary.synagogue.guests + summary.synagogue.children})</div>
          <div style={{display: 'flex', justifyContent: 'space-around', width: '100%', borderTop: '1px solid #cbd5e0', paddingTop: '8px'}}>
            <div>
              <div className="summary-value" style={{fontSize: '20px'}}>{summary.synagogue.guests}</div>
              <div className="summary-label" style={{fontSize: '12px'}}>👨‍👩‍👧‍👦 מבוגרים</div>
            </div>
            <div>
              <div className="summary-value" style={{fontSize: '20px'}}>{summary.synagogue.children}</div>
              <div className="summary-label" style={{fontSize: '12px'}}>👶 ילדים</div>
            </div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-label" style={{marginBottom: '10px', fontWeight: 'bold'}}>🍽️ למסעדה ({summary.restaurant.guests + summary.restaurant.children})</div>
          <div style={{display: 'flex', justifyContent: 'space-around', width: '100%', borderTop: '1px solid #cbd5e0', paddingTop: '8px'}}>
            <div>
              <div className="summary-value" style={{fontSize: '20px'}}>{summary.restaurant.guests}</div>
              <div className="summary-label" style={{fontSize: '12px'}}>👨‍👩‍👧‍👦 מבוגרים</div>
            </div>
            <div>
              <div className="summary-value" style={{fontSize: '20px'}}>{summary.restaurant.children}</div>
              <div className="summary-label" style={{fontSize: '12px'}}>👶 ילדים</div>
            </div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-value">{summary.notComing}</div>
          <div className="summary-label">❌ לא מגיעים</div>
        </div>

        <div className="summary-card">
          <div className="summary-value">{summary.maybe}</div>
          <div className="summary-label">❓ אולי</div>
        </div>
      </div>

      <div className="filters-container">
        <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>📋 הכל</button>
        {(eventConfig?.attendanceOptions || []).map(opt => (
          <button
            key={opt.value}
            className={`filter-btn ${filter === opt.value ? 'active' : ''}`}
            onClick={() => setFilter(opt.value)}
          >
            {opt.emoji} {opt.label}
          </button>
        ))}
      </div>

      {isEditing && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{currentId ? 'עריכת רשומה' : 'הוספת רשומה'}</h3>
            <form onSubmit={handleSave}>
              <div className="form-grid">
                <label>שם: <input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required /></label>
                <label>טלפון: <input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required /></label>
                <label>הגעה: 
                  <select value={formData.attendance} onChange={e => setFormData({...formData, attendance: e.target.value})}>
                    {(eventConfig?.attendanceOptions || []).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                <label>אוכל: 
                  <select value={formData.food} onChange={e => setFormData({...formData, food: e.target.value})}>
                    <option value="">בחר העדפה</option>
                    {foodOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                    {/* Preserve existing value if not in list (e.g. multiple selections) */}
                    {formData.food && !foodOptions.includes(formData.food) && (
                      <option value={formData.food}>{formData.food}</option>
                    )}
                  </select>
                </label>
                <label>מבוגרים: <input type="number" value={formData.guests} onChange={e => setFormData({...formData, guests: Number(e.target.value)})} /></label>
                <label>ילדים: <input type="number" value={formData.children} onChange={e => setFormData({...formData, children: Number(e.target.value)})} /></label>
                
                <label className="full-width">הודעה: <textarea value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} /></label>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setIsEditing(false)} className="action-btn cancel-btn">ביטול</button>
                <button type="submit" className="action-btn save-btn">שמור</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="rsvp-table">
          <thead>
            <tr>
              <th>שם</th>
              <th>טלפון</th>
              <th className="actions-column">הגעה</th>
              <th>מבוגרים</th>
              <th>ילדים</th>
              <th>אוכל</th>
              <th>הודעה</th>
              <th className="actions-column">פעולות</th>
            </tr>
          </thead>
          <tbody>
            {filteredRsvps.map(r => (
              <tr key={r.ID} style={getRowStyle(r.Attendance)}>
                <td>{r.Name}</td>
                <td>{r.Phone}</td>
                <td className="actions-column">{attendanceMap[r.Attendance] || r.Attendance}</td>
                <td>{r.Guests}</td>
                <td>{r.Children}</td>
                <td>{r.Food}</td>
                <td>{r.Message}</td>
                <td className="actions-column">
                  <button onClick={() => handleEdit(r)} className="action-btn edit-btn" title="ערוך">✏️</button>
                  <button onClick={() => handleDelete(r.ID)} className="action-btn delete-btn" title="מחק">🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Backoffice;
